import { useState, useMemo } from 'react';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { usePermissions, PERMISSIONS } from '@/hooks/usePermissions';
import {
  useSystemLookups,
  useCreateLookup,
  useUpdateLookup,
  useDeleteLookup,
  LOOKUP_CATEGORIES,
  CATEGORY_LABELS,
  type LookupCategory,
  type SystemLookup,
  type CreateLookupPayload,
  type UpdateLookupPayload,
} from './hooks/useSystemLookups';
import { EntityTypesPage } from './EntityTypesPage';
import { TagsPage } from './TagsPage';
import { TaxRatesPage } from './TaxRatesPage';
import { NoteCategoriesPage } from './NoteCategoriesPage';

/**
 * Top-level tab options for the System Lookups section.
 * Includes standalone CRUD pages (Entity Types, Tags, Tax Rates, Note Categories) alongside lookup categories.
 */
type TopTab = 'lookups' | 'entity-types' | 'tags' | 'tax-rates' | 'note-categories';

const TOP_TABS: { id: TopTab; label: string }[] = [
  { id: 'lookups', label: 'Lookups' },
  { id: 'entity-types', label: 'Entity Types' },
  { id: 'tags', label: 'Tags' },
  { id: 'tax-rates', label: 'Tax Rates' },
  { id: 'note-categories', label: 'Note Categories' },
];

/**
 * SystemLookupsPage — Combined management page for all tenant-scoped configuration data.
 * Includes tabs for: Lookups (by category), Entity Types, Tags, and Tax Rates.
 */
export function SystemLookupsPage() {
  const [activeTopTab, setActiveTopTab] = useState<TopTab>('lookups');

  return (
    <div className="space-y-6">
      {/* Top-Level Tabs */}
      <div className="border-b border-border dark:border-border-dark">
        <div className="flex gap-1">
          {TOP_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTopTab(tab.id)}
              className={`rounded-t-lg px-4 py-2 text-sm font-medium transition-colors ${
                activeTopTab === tab.id
                  ? 'tab-active border-b-2 bg-white dark:bg-surface-dark'
                  : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      {activeTopTab === 'lookups' && <LookupsContent />}
      {activeTopTab === 'entity-types' && <EntityTypesPage />}
      {activeTopTab === 'tags' && <TagsPage />}
      {activeTopTab === 'tax-rates' && <TaxRatesPage />}
      {activeTopTab === 'note-categories' && <NoteCategoriesPage />}
    </div>
  );
}

// ─── Lookups Content (original system lookups with category sub-tabs) ─────────

function LookupsContent() {
  const { can } = usePermissions();
  const [activeCategory, setActiveCategory] = useState<LookupCategory>('lead_stages');
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingLookup, setEditingLookup] = useState<SystemLookup | null>(null);

  const { data: lookups, isLoading } = useSystemLookups(activeCategory);
  const createMutation = useCreateLookup();
  const updateMutation = useUpdateLookup();
  const deleteMutation = useDeleteLookup();

  // For discount_definitions, require billing.create_discount permission
  const canManageDiscounts =
    activeCategory !== 'discount_definitions' || can(PERMISSIONS.BILLING_CREATE_DISCOUNT);

  const sortedLookups = useMemo(
    () => [...(lookups ?? [])].sort((a, b) => a.displayOrder - b.displayOrder),
    [lookups],
  );

  const activeCount = sortedLookups.filter((l) => l.isActive).length;

  const handleCreate = () => {
    setEditingLookup(null);
    setIsEditorOpen(true);
  };

  const handleEdit = (lookup: SystemLookup) => {
    setEditingLookup(lookup);
    setIsEditorOpen(true);
  };

  const handleSave = (data: CreateLookupPayload | UpdateLookupPayload) => {
    if (editingLookup) {
      updateMutation.mutate(
        { id: editingLookup.id, data: data as UpdateLookupPayload },
        {
          onSuccess: () => {
            setIsEditorOpen(false);
            setEditingLookup(null);
          },
        },
      );
    } else {
      createMutation.mutate(data as CreateLookupPayload, {
        onSuccess: () => {
          setIsEditorOpen(false);
          setEditingLookup(null);
        },
      });
    }
  };

  const handleToggleActive = (lookup: SystemLookup) => {
    if (lookup.isActive) {
      deleteMutation.mutate(lookup.id);
    } else {
      updateMutation.mutate({ id: lookup.id, data: { is_active: true } });
    }
  };

  return (
    <div className="space-y-6">
      {/* Category Tabs */}
      <div className="flex flex-wrap gap-2">
        {LOOKUP_CATEGORIES.filter(
          (cat) => cat !== 'discount_definitions' || can(PERMISSIONS.BILLING_CREATE_DISCOUNT),
        ).map((category) => (
          <button
            key={category}
            type="button"
            onClick={() => setActiveCategory(category)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
              activeCategory === category
                ? 'bg-brand-600 text-white'
                : 'bg-surface-secondary text-gray-600 hover:bg-surface-tertiary dark:bg-surface-dark-secondary dark:text-gray-300 dark:hover:bg-surface-dark-tertiary'
            }`}
          >
            {CATEGORY_LABELS[category]}
          </button>
        ))}
      </div>

      {/* Lookups Table */}
      <Card>
        <CardHeader
          title={CATEGORY_LABELS[activeCategory]}
          description={`Manage ${CATEGORY_LABELS[activeCategory].toLowerCase()} available in your firm.`}
          action={
            canManageDiscounts ? (
              <Button size="sm" onClick={handleCreate}>
                Add Item
              </Button>
            ) : undefined
          }
        />
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div
                className="h-8 w-8 animate-spin rounded-full border-4 border-brand-600 border-t-transparent"
                role="status"
                aria-label="Loading lookups"
              />
            </div>
          ) : sortedLookups.length === 0 ? (
            <p className="py-4 text-sm text-gray-500 dark:text-gray-400">
              No {CATEGORY_LABELS[activeCategory].toLowerCase()} configured yet. Add your first item
              to get started.
            </p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" role="table">
                  <thead>
                    <tr className="border-b border-border text-left dark:border-border-dark">
                      <th className="pb-3 pr-4 font-medium text-gray-500 dark:text-gray-400">
                        Order
                      </th>
                      <th className="pb-3 pr-4 font-medium text-gray-500 dark:text-gray-400">
                        Code
                      </th>
                      <th className="pb-3 pr-4 font-medium text-gray-500 dark:text-gray-400">
                        Label
                      </th>
                      {activeCategory === 'contact_categories' && (
                        <th className="pb-3 pr-4 font-medium text-gray-500 dark:text-gray-400">
                          Nature
                        </th>
                      )}
                      {activeCategory === 'discount_definitions' && (
                        <>
                          <th className="pb-3 pr-4 font-medium text-gray-500 dark:text-gray-400">
                            Type
                          </th>
                          <th className="pb-3 pr-4 font-medium text-gray-500 dark:text-gray-400">
                            Value
                          </th>
                          <th className="pb-3 pr-4 font-medium text-gray-500 dark:text-gray-400">
                            Mode
                          </th>
                        </>
                      )}
                      <th className="pb-3 pr-4 font-medium text-gray-500 dark:text-gray-400">
                        Description
                      </th>
                      <th className="pb-3 pr-4 font-medium text-gray-500 dark:text-gray-400">
                        Status
                      </th>
                      <th className="pb-3 font-medium text-gray-500 dark:text-gray-400">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border dark:divide-border-dark">
                    {sortedLookups.map((lookup) => (
                      <tr key={lookup.id}>
                        <td className="py-3 pr-4 text-gray-600 dark:text-gray-300">
                          {lookup.displayOrder}
                        </td>
                        <td className="py-3 pr-4 font-mono text-xs text-gray-500 dark:text-gray-400">
                          {lookup.code}
                        </td>
                        <td className="py-3 pr-4 font-medium text-gray-900 dark:text-gray-100">
                          {lookup.label}
                          {lookup.isDefault && (
                            <Badge variant="info" size="sm" className="ml-2">
                              Default
                            </Badge>
                          )}
                        </td>
                        {activeCategory === 'contact_categories' && (
                          <td className="py-3 pr-4">
                            <Badge
                              variant={
                                (lookup.metadata?.nature as string) === 'client'
                                  ? 'success'
                                  : 'warning'
                              }
                              size="sm"
                            >
                              {(lookup.metadata?.nature as string) === 'client'
                                ? 'Client'
                                : 'Prospect'}
                            </Badge>
                          </td>
                        )}
                        {activeCategory === 'discount_definitions' && (
                          <>
                            <td className="py-3 pr-4">
                              <Badge
                                variant={
                                  (lookup.metadata?.discount_type as string) === 'value'
                                    ? 'info'
                                    : 'warning'
                                }
                                size="sm"
                              >
                                {(lookup.metadata?.discount_type as string) === 'value'
                                  ? 'Value'
                                  : 'Percentage'}
                              </Badge>
                            </td>
                            <td className="py-3 pr-4 font-medium text-gray-900 dark:text-gray-100">
                              {(lookup.metadata?.discount_type as string) === 'value'
                                ? String(lookup.metadata?.discount_value ?? 0)
                                : `${lookup.metadata?.discount_value ?? 0}%`}
                            </td>
                            <td className="py-3 pr-4">
                              <Badge
                                variant={
                                  (lookup.metadata?.discount_mode as string) === 'manual'
                                    ? 'warning'
                                    : ((lookup.metadata?.service_rules as unknown[]) ?? []).length >
                                        0
                                      ? 'info'
                                      : 'default'
                                }
                                size="sm"
                              >
                                {(lookup.metadata?.discount_mode as string) === 'manual'
                                  ? 'Manual'
                                  : ((lookup.metadata?.service_rules as unknown[]) ?? []).length > 0
                                    ? 'Rule-Based'
                                    : 'Standard'}
                              </Badge>
                            </td>
                          </>
                        )}
                        <td className="py-3 pr-4 text-gray-600 dark:text-gray-300">
                          {lookup.description || '—'}
                        </td>
                        <td className="py-3 pr-4">
                          <Badge variant={lookup.isActive ? 'success' : 'default'} size="sm" dot>
                            {lookup.isActive ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        <td className="py-3">
                          <div className="flex items-center gap-1">
                            {canManageDiscounts && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEdit(lookup)}
                                >
                                  Edit
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleToggleActive(lookup)}
                                >
                                  {lookup.isActive ? 'Deactivate' : 'Activate'}
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                {activeCount} active item{activeCount !== 1 ? 's' : ''} in{' '}
                {CATEGORY_LABELS[activeCategory]}
              </p>
            </>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      {isEditorOpen && (
        <LookupEditorModal
          lookup={editingLookup}
          category={activeCategory}
          onSave={handleSave}
          onClose={() => {
            setIsEditorOpen(false);
            setEditingLookup(null);
          }}
          isSaving={createMutation.isPending || updateMutation.isPending}
        />
      )}
    </div>
  );
}

// ─── Lookup Editor Modal ──────────────────────────────────────────────────────

interface LookupEditorModalProps {
  lookup: SystemLookup | null;
  category: LookupCategory;
  onSave: (data: CreateLookupPayload | UpdateLookupPayload) => void;
  onClose: () => void;
  isSaving: boolean;
}

function LookupEditorModal({
  lookup,
  category,
  onSave,
  onClose,
  isSaving,
}: LookupEditorModalProps) {
  const [form, setForm] = useState({
    code: lookup?.code ?? '',
    label: lookup?.label ?? '',
    description: lookup?.description ?? '',
    displayOrder: lookup?.displayOrder ?? 0,
    isDefault: lookup?.isDefault ?? false,
    nature: (lookup?.metadata?.nature as string) ?? '',
  });

  // Discount-specific state
  const [discountType, setDiscountType] = useState<string>(
    (lookup?.metadata?.discount_type as string) ?? 'percentage',
  );
  const [discountValue, setDiscountValue] = useState<number>(
    (lookup?.metadata?.discount_value as number) ?? 0,
  );
  const [discountDefinitionMode, setDiscountDefinitionMode] = useState<
    'standard' | 'rule_based' | 'manual'
  >(
    (lookup?.metadata?.discount_mode as 'standard' | 'rule_based' | 'manual') ??
      (((lookup?.metadata?.service_rules as unknown[]) ?? []).length > 0
        ? 'rule_based'
        : 'standard'),
  );
  const [selectedContactCategories, setSelectedContactCategories] = useState<string[]>(
    (lookup?.metadata?.contact_categories as string[]) ?? [],
  );
  const [serviceRules, setServiceRules] = useState<
    Array<{ service_type: string; discount_type: string; discount_value: number }>
  >(
    (lookup?.metadata?.service_rules as Array<{
      service_type: string;
      discount_type: string;
      discount_value: number;
    }>) ?? [],
  );

  // Fetch related lookups for discount_definitions
  const { data: contactCategories } = useSystemLookups(
    'contact_category',
    category === 'discount_definitions',
  );
  const { data: serviceTypes } = useSystemLookups(
    'service_types',
    category === 'discount_definitions',
  );

  const [error, setError] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate nature is required for contact_categories
    if (category === 'contact_categories' && !form.nature) {
      setError('Nature is required for contact categories');
      return;
    }

    // Validate no duplicate service types in discount rules
    if (category === 'discount_definitions' && serviceRules.length > 0) {
      const serviceTypeCodes = serviceRules.map((r) => r.service_type).filter((s) => s !== '');
      const duplicates = serviceTypeCodes.filter(
        (code, idx) => serviceTypeCodes.indexOf(code) !== idx,
      );
      if (duplicates.length > 0) {
        setError('Each service type can only be added once per discount rule.');
        return;
      }
      const hasEmpty = serviceRules.some((r) => !r.service_type);
      if (hasEmpty) {
        setError('Please select a service type for all rules.');
        return;
      }
    }

    // Auto-generate code from label for contact_categories
    const code =
      category === 'contact_categories'
        ? form.label
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '_')
            .replace(/^_|_$/g, '')
        : form.code;

    if (lookup) {
      // Update — don't send code (it's immutable)
      const payload: UpdateLookupPayload = {
        label: form.label,
        description: form.description || null,
        display_order: form.displayOrder,
        is_default: form.isDefault,
      };
      if (category === 'contact_categories') {
        payload.metadata = { ...(lookup.metadata ?? {}), nature: form.nature };
      }
      if (category === 'discount_definitions') {
        payload.metadata = {
          discount_type: discountType,
          discount_value: discountValue,
          discount_mode: discountDefinitionMode,
          contact_categories: selectedContactCategories,
          service_rules: serviceRules,
        };
      }
      onSave(payload);
    } else {
      // Create
      const payload: CreateLookupPayload = {
        category,
        code,
        label: form.label,
        description: form.description || undefined,
        display_order: form.displayOrder,
        is_default: form.isDefault,
      };
      if (category === 'contact_categories') {
        payload.metadata = { nature: form.nature };
      }
      if (category === 'discount_definitions') {
        payload.metadata = {
          discount_type: discountType,
          discount_value: discountValue,
          discount_mode: discountDefinitionMode,
          contact_categories: selectedContactCategories,
          service_rules: serviceRules,
        };
      }
      onSave(payload);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={
        lookup ? `Edit ${CATEGORY_LABELS[category]} Item` : `New ${CATEGORY_LABELS[category]} Item`
      }
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400">
            {error}
          </div>
        )}
        <Input
          label="Code"
          value={form.code}
          onChange={(e) =>
            setForm((prev) => ({
              ...prev,
              code: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'),
            }))
          }
          required
          maxLength={100}
          placeholder="e.g., t1_personal"
          hint="Lowercase identifier. Cannot be changed after creation."
          disabled={!!lookup}
        />
        <Input
          label="Label"
          value={form.label}
          onChange={(e) => setForm((prev) => ({ ...prev, label: e.target.value }))}
          required
          maxLength={200}
          placeholder="e.g., T1 Personal Tax Return"
        />
        <Input
          label="Description"
          value={form.description}
          onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
          maxLength={500}
          placeholder="Optional description"
        />
        {category === 'contact_categories' && (
          <div>
            <label
              htmlFor="lookup-nature"
              className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              Nature <span className="text-red-500">*</span>
            </label>
            <select
              id="lookup-nature"
              value={form.nature}
              onChange={(e) => setForm((prev) => ({ ...prev, nature: e.target.value }))}
              className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm dark:border-border-dark dark:bg-gray-800 dark:text-gray-100"
              required
            >
              <option value="">Select nature...</option>
              <option value="prospect">Prospect</option>
              <option value="client">Client</option>
            </select>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Determines behavior: prospect-nature categories can be converted to client-nature
              categories.
            </p>
          </div>
        )}
        {category === 'discount_definitions' && (
          <div className="space-y-4">
            {/* Discount Mode Toggle */}
            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Discount Mode
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setDiscountDefinitionMode('standard');
                    setServiceRules([]);
                  }}
                  className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                    discountDefinitionMode === 'standard'
                      ? 'bg-brand-600 text-white'
                      : 'border border-border bg-white text-gray-700 hover:bg-gray-50 dark:border-border-dark dark:bg-gray-800 dark:text-gray-300'
                  }`}
                >
                  Standard
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setDiscountDefinitionMode('rule_based');
                    if (serviceRules.length === 0) {
                      setServiceRules([
                        { service_type: '', discount_type: 'percentage', discount_value: 0 },
                      ]);
                    }
                  }}
                  className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                    discountDefinitionMode === 'rule_based'
                      ? 'bg-brand-600 text-white'
                      : 'border border-border bg-white text-gray-700 hover:bg-gray-50 dark:border-border-dark dark:bg-gray-800 dark:text-gray-300'
                  }`}
                >
                  Rule-Based (Per Service)
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setDiscountDefinitionMode('manual');
                    setServiceRules([]);
                  }}
                  className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                    discountDefinitionMode === 'manual'
                      ? 'bg-brand-600 text-white'
                      : 'border border-border bg-white text-gray-700 hover:bg-gray-50 dark:border-border-dark dark:bg-gray-800 dark:text-gray-300'
                  }`}
                >
                  Manual
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Standard applies a single discount to all items. Rule-based sets different discounts
                per service type. Manual allows users to enter discounts freely on each line item.
              </p>
            </div>

            {/* Standard Mode: Type & Value */}
            {discountDefinitionMode === 'standard' && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Type
                  </label>
                  <select
                    value={discountType}
                    onChange={(e) => setDiscountType(e.target.value)}
                    className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm dark:border-border-dark dark:bg-gray-800 dark:text-gray-100"
                  >
                    <option value="percentage">Percentage (%)</option>
                    <option value="value">Value</option>
                  </select>
                </div>
                <Input
                  label="Amount"
                  type="number"
                  value={String(discountValue)}
                  onChange={(e) => setDiscountValue(Number(e.target.value))}
                  min={0}
                  step="0.01"
                  placeholder={discountType === 'percentage' ? 'e.g., 10' : 'e.g., 50'}
                />
              </div>
            )}

            {/* Rule-Based Mode: Per-Service Rules */}
            {discountDefinitionMode === 'rule_based' && (
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Service Rules
                  </label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setServiceRules((prev) => [
                        ...prev,
                        { service_type: '', discount_type: 'percentage', discount_value: 0 },
                      ])
                    }
                  >
                    + Add
                  </Button>
                </div>
                <div className="space-y-2">
                  {serviceRules.map((rule, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-2 rounded-lg border border-border p-2 dark:border-border-dark"
                    >
                      <select
                        value={rule.service_type}
                        onChange={(e) =>
                          setServiceRules((prev) =>
                            prev.map((r, i) =>
                              i === idx ? { ...r, service_type: e.target.value } : r,
                            ),
                          )
                        }
                        className="flex-1 rounded border border-border bg-white px-2 py-1.5 text-sm dark:border-border-dark dark:bg-gray-800 dark:text-gray-100"
                      >
                        <option value="">Service type...</option>
                        {(serviceTypes ?? [])
                          .filter((s) => s.isActive)
                          .filter(
                            (s) =>
                              s.code === rule.service_type ||
                              !serviceRules.some((r, i) => i !== idx && r.service_type === s.code),
                          )
                          .map((st) => (
                            <option key={st.code} value={st.code}>
                              {st.label}
                            </option>
                          ))}
                      </select>
                      <select
                        value={rule.discount_type}
                        onChange={(e) =>
                          setServiceRules((prev) =>
                            prev.map((r, i) =>
                              i === idx ? { ...r, discount_type: e.target.value } : r,
                            ),
                          )
                        }
                        className="w-24 rounded border border-border bg-white px-2 py-1.5 text-sm dark:border-border-dark dark:bg-gray-800 dark:text-gray-100"
                      >
                        <option value="percentage">%</option>
                        <option value="value">Value</option>
                      </select>
                      <input
                        type="number"
                        value={rule.discount_value}
                        onChange={(e) =>
                          setServiceRules((prev) =>
                            prev.map((r, i) =>
                              i === idx ? { ...r, discount_value: Number(e.target.value) } : r,
                            ),
                          )
                        }
                        min={0}
                        step="0.01"
                        placeholder="0"
                        className="w-20 rounded border border-border bg-white px-2 py-1.5 text-sm dark:border-border-dark dark:bg-gray-800 dark:text-gray-100"
                      />
                      <button
                        type="button"
                        onClick={() => setServiceRules((prev) => prev.filter((_, i) => i !== idx))}
                        className="text-gray-400 hover:text-red-500"
                        aria-label="Remove rule"
                      >
                        <svg
                          className="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Contact Categories */}
            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Contact Category
              </label>
              <div className="flex flex-wrap gap-2">
                {(contactCategories ?? [])
                  .filter((c) => c.isActive)
                  .map((cat) => {
                    const selected = selectedContactCategories.includes(cat.code);
                    return (
                      <button
                        key={cat.code}
                        type="button"
                        onClick={() => {
                          if (selected) {
                            setSelectedContactCategories((prev) =>
                              prev.filter((c) => c !== cat.code),
                            );
                          } else {
                            setSelectedContactCategories((prev) => [...prev, cat.code]);
                          }
                        }}
                        className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                          selected
                            ? 'bg-brand-100 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-400'
                        }`}
                      >
                        {cat.label}
                      </button>
                    );
                  })}
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Select contact categories this discount applies to. Leave empty for all.
              </p>
            </div>
          </div>
        )}
        <Input
          label="Display Order"
          type="number"
          value={String(form.displayOrder)}
          onChange={(e) => setForm((prev) => ({ ...prev, displayOrder: Number(e.target.value) }))}
          min={0}
          hint="Controls the order in which items appear in lists"
        />
        <div className="flex items-center gap-3">
          <input
            id="lookup-is-default"
            type="checkbox"
            checked={form.isDefault}
            onChange={(e) => setForm((prev) => ({ ...prev, isDefault: e.target.checked }))}
            className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
          />
          <label
            htmlFor="lookup-is-default"
            className="text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            Mark as default value
          </label>
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            loading={isSaving}
            disabled={!form.code.trim() || !form.label.trim()}
          >
            {lookup ? 'Update' : 'Create'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
